import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import Link, { LinkProps } from 'next/link';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useCommerceAPI } from '~/features/commerce-api/contexts';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useProductDisplayPrices } from '~/features/product-details/components/ProductOverview/hooks/useProductDisplayPrices';
import {
    PRODUCT_DATA_GC_TIME,
    PRODUCT_DATA_STALE_TIME,
} from '~/features/product-details/constants';
import { usePDPData } from '~/features/product-details/context';
import { sortIDsForQueryKeys } from '~/features/product-details/utilities';
import { TranslationKey } from '~/lib';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { getSalesforceProductImages } from '~/lib/salesforce/utilities/product-images';
import { useProductHandler } from '~/services/search/useProductHandler';
import { Button, Typography } from '~/shared/components';
import { useEvents } from '~/shared/hooks/useEvents';
import { transformSalesforceProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_ID, GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { toBoolean } from '~/shared/utils/toBoolean';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductRecommendations.module.scss';
import { ProductRecommendationsSkeleton } from './ProductRecommendationsSkeleton';

const LIST_ID = GTM_LIST_ID.COMPLEMENTARY_PRODUCTS;
const LIST_NAME = GTM_LIST_NAME.COMPLEMENTARY;

const getSingularCTA = (product: SalesforceProduct) => {
    const { c_primaryButton: primary, c_secondaryButton: secondary } =
        product.c_buyButtonsObj || {};

    if (primary && !toBoolean(primary.isDisabled) && toBoolean(primary.shouldRender)) {
        return primary;
    } else if (secondary && !toBoolean(secondary.isDisabled) && toBoolean(secondary.shouldRender)) {
        return secondary;
    }

    return null; // Return null if no buttons meet the criteria
};

type RecommendationProps = Omit<LinkProps, 'href' | 'onClick'> & {
    className?: string;
    product: SalesforceProduct;
    onClick?(recommendation: SalesforceProduct): void;
    parentTitle?: string;
};

const Recommendation: React.FC<RecommendationProps> = ({
    className,
    product,
    parentTitle,
    onClick,
    ...rest
}) => {
    const { convertPriceToLocale } = useUtils();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const productHandler = useProductHandler();
    const prices = useProductDisplayPrices(product);
    const { productClickEvent } = useEvents(frame);

    const image = useMemo(() => getSalesforceProductImages(product, {})?.at(0), [product]);
    const button = useMemo(() => getSingularCTA(product), [product]);
    const url = product.c_pageURL;

    const productEventData = useMemo(
        () => transformSalesforceProductToDataLayer(product),
        [product],
    );

    const onButtonClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            const eventType = button?.translationKey;

            /*
             * Adding to the basket is the only action that has to happen here. Everything else
             * this button can say - reserving in store, coming soon - is answered on the
             * product's own page, which is where the card's link already points, so the click is
             * left to bubble and Next handles the navigation.
             */
            if (eventType !== 'addtocart') return;

            event.preventDefault();
            event.stopPropagation();

            productHandler(LIST_ID, {
                type: eventType,
                productEventData,
                payload: {
                    productId: product.id,
                    orderable: product.inventory?.orderable,
                    quantity: 1,
                    url,
                },
                quantity: 1,
            });

            onClick?.(product);
        },
        [product, productHandler],
    );

    const onProductClick = useCallback(
        (_event: React.MouseEvent<HTMLAnchorElement>) => {
            productClickEvent(LIST_ID, `${LIST_NAME} - ${parentTitle}`, productEventData);

            onClick?.(product);
        },
        [product, onClick],
    );

    // The whole card is a link, so a product without a page has nothing to offer.
    if (!url) return null;

    return (
        <Link
            className={clsx(styles.recommendation, className)}
            {...rest}
            href={url}
            onClick={onProductClick}
        >
            <div className={styles.recommendationImage}>
                {!!image?.link && (
                    <Image
                        src={image.link}
                        width={77}
                        height={77}
                        sizes="77px"
                        alt={image.alt ?? product.name ?? ''}
                    />
                )}
            </div>

            <div className={styles.recommendationOverview}>
                <div className={styles.recommendationDetails}>
                    <Typography variant="paragraph-S" as="p">
                        {product.brand}
                    </Typography>

                    <Typography variant="paragraph-M" as="p" weight="medium">
                        {product.name}
                    </Typography>

                    <ul className={styles.recommendationPriceList}>
                        {prices.map((price, index) => (
                            <li
                                key={`${price.type}-${index}`}
                                className={styles.recommendationPriceItem}
                                data-price-type={price.type}
                            >
                                <Typography
                                    className={styles.recommendationPriceAmount}
                                    variant="paragraph-S"
                                    weight="medium"
                                >
                                    {convertPriceToLocale(price.value)}
                                </Typography>

                                <Typography
                                    className={styles.recommendationPriceLabel}
                                    variant="paragraph-XS"
                                >
                                    {price.label}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </div>

                {button?.shouldRender && (
                    <div className={styles.recommendationActions}>
                        <Button
                            size="small"
                            small
                            variant="pdp"
                            disabled={button.isDisabled}
                            onClick={onButtonClick}
                        >
                            {translate(`plp.${button.translationKey}` as TranslationKey)}
                        </Button>
                    </div>
                )}
            </div>
        </Link>
    );
};

export const ProductRecommendations: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { product, currency } = usePDPData();
    const { shopperProducts } = useCommerceAPI();
    const { isPlusMember } = useCustomer();
    const { productsImpressionsEvent } = useEvents(frame);
    const { inView, ref } = useInView({ threshold: 0 });

    const recommendationIds = useMemo(
        () =>
            (product.recommendations || [])
                .map((item) => item.recommendedItemId)
                .filter((item): item is string => Boolean(item)),
        [product],
    );

    const { data: recommendations, isPending } = useQuery({
        queryKey: ['Salesforce', 'Recommendations', sortIDsForQueryKeys(recommendationIds)],
        queryFn: async () => {
            const response = await shopperProducts?.getProducts({
                parameters: {
                    ids: recommendationIds?.join(','),
                    currency,
                },
            });

            return (response?.data || []) as SalesforceProduct[];
        },
        enabled: !!recommendationIds?.length,
        staleTime: PRODUCT_DATA_STALE_TIME,
        gcTime: PRODUCT_DATA_GC_TIME,
    });

    const heading = translate('pdp.overview.addOnsTitle');

    // A disabled query stays pending forever, so the ids are what say anything is on its way.
    const isLoading = !!recommendationIds.length && isPending;

    useEffect(() => {
        if (!inView || !recommendations?.length) return;

        productsImpressionsEvent(
            LIST_ID,
            `${LIST_NAME} - ${heading}`,
            // Position is added here because it belongs to this list, not to the product.
            recommendations.map((item, index) => ({
                ...transformSalesforceProductToDataLayer(item, { isPlusMember }),
                position: index,
            })),
        );
    }, [inView, recommendations]);

    if (!isLoading && !recommendations?.length) return null;

    return (
        <div ref={ref} {...props}>
            {heading && (
                <Typography className={styles.recommendationsTitle} variant="title-XS" as="h3">
                    {heading}
                </Typography>
            )}

            {isLoading ? (
                <ProductRecommendationsSkeleton count={recommendationIds.length} />
            ) : (
                <ul className={styles.recommendationsList}>
                    {recommendations?.map((item) => (
                        <Recommendation
                            key={item.id}
                            className={styles.recommendationsItem}
                            parentTitle={heading}
                            product={item}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};
